import type { ChatResponse } from "@/types/chat";
import type { WorkspacePersona } from "@/config/workspacePersona";
import {
  CHAT_RECOMMENDATIONS,
  getRecommendationById,
  isRecommendationVisibleForPersona,
  type ChatRecommendationDefinition,
} from "@/config/chatRecommendations";
import {
  CONCEPT_DEFS,
  REC_CONCEPTS,
  tokenizeMessage,
  type ConceptName,
} from "@/lib/chatIntentConcepts";

/**
 * Resolve which (if any) destination to surface in the chat right now.
 *
 * Priority:
 *   1. Server hint   — `response.metadata.recommendation.id` (n8n decided)
 *   2. Client matcher — keyword scan over the user's last message
 *
 * Suppression:
 *   - Once a user has seen a recommendation (dismissed or opened it), we don't
 *     auto-suggest the same one again. The exception is when the user types an
 *     "explicit ask" phrase (e.g. the literal tool name) — we read that as
 *     "I want this", reset the suppression for that id, and pop the card.
 *
 *   - The `seenIds` set is supplied by `useSeenRecommendations` and persists
 *     in localStorage per user.
 */
export type ResolvedRecommendation = {
  /** The full registry entry to render. */
  definition: ChatRecommendationDefinition;
  /** Where the recommendation came from — useful for analytics & debugging. */
  source: "server" | "client" | "explicit_ask";
  /** Optional override blurb supplied by the server hint. */
  reason?: string;
};

/** Lowercased word-ish boundary check that tolerates punctuation around the trigger. */
function messageContainsTrigger(messageLower: string, trigger: string): boolean {
  if (!trigger) return false;
  const t = trigger.toLowerCase();
  const idx = messageLower.indexOf(t);
  if (idx === -1) return false;

  const before = idx === 0 ? "" : messageLower[idx - 1];
  const after = messageLower[idx + t.length] ?? "";

  const isWordChar = (c: string) => /[a-z0-9]/.test(c);
  const triggerStartsWithWordChar = isWordChar(t[0]);
  const triggerEndsWithWordChar = isWordChar(t[t.length - 1]);

  if (triggerStartsWithWordChar && before && isWordChar(before)) return false;
  if (triggerEndsWithWordChar && after && isWordChar(after)) return false;
  return true;
}

function messageMatchesAnyExplicitAsk(
  messageLower: string,
  def: ChatRecommendationDefinition
): boolean {
  return def.explicitAskTriggers.some((t) => messageContainsTrigger(messageLower, t));
}

/** Does the message express this concept (single-word token OR multi-word phrase)? */
function messageHasConcept(
  concept: ConceptName,
  messageLower: string,
  tokens: ReadonlySet<string>
): boolean {
  const def = CONCEPT_DEFS[concept];
  for (const w of def.words) {
    if (tokens.has(w)) return true;
  }
  if (def.phrases) {
    for (const phrase of def.phrases) {
      if (messageContainsTrigger(messageLower, phrase)) return true;
    }
  }
  return false;
}

type DefScore = {
  score: number;
  /** True when something strong enough to justify a suggestion matched. */
  qualifies: boolean;
};

/**
 * Score a destination by combining two signals:
 *  1. Literal trigger phrases (high confidence — close to the tool name).
 *  2. Intent concepts (everyday language → canonical topic).
 *
 * A destination only "qualifies" when it has a literal phrase hit OR at least
 * one PRIMARY concept. Supporting concepts (e.g. "conversation") add score but
 * never trigger a suggestion on their own — "I need to talk to someone" should
 * not arbitrarily pick a chat space.
 */
function scoreDefinition(
  def: ChatRecommendationDefinition,
  messageLower: string,
  tokens: ReadonlySet<string>
): DefScore {
  let score = 0;
  let phraseHits = 0;
  for (const trigger of def.triggers) {
    if (messageContainsTrigger(messageLower, trigger)) {
      score += 6 + trigger.length * 0.2;
      phraseHits += 1;
    }
  }

  let primaryHits = 0;
  const concepts = REC_CONCEPTS[def.id] ?? [];
  for (const concept of concepts) {
    if (!messageHasConcept(concept, messageLower, tokens)) continue;
    const isPrimary = CONCEPT_DEFS[concept].primary;
    score += isPrimary ? 3 : 1;
    if (isPrimary) primaryHits += 1;
  }

  const qualifies = phraseHits > 0 || primaryHits > 0;
  if (!qualifies) return { score: 0, qualifies: false };

  // Tools (structured workflows) win ties over open chats — a high-signal
  // "behavior incident" should beat a generic "kids" → Connect with Students.
  if (def.kind === "tool") score += 2;
  return { score, qualifies: true };
}

type MatchOptions = {
  persona: WorkspacePersona;
  /** Recommendation ids that have already been shown once. Auto-suggest is suppressed for these. */
  seenIds?: ReadonlySet<string>;
};

/** Client-side matcher. Returns the highest-scoring recommendation, or null. */
export function matchRecommendationFromUserMessage(
  message: string,
  { persona, seenIds }: MatchOptions
): ResolvedRecommendation | null {
  if (!message || !message.trim()) return null;
  const messageLower = message.toLowerCase();
  const tokens = tokenizeMessage(message);

  // Pass 1: explicit asks always win and bypass suppression.
  for (const def of CHAT_RECOMMENDATIONS) {
    if (!isRecommendationVisibleForPersona(def, persona)) continue;
    if (messageMatchesAnyExplicitAsk(messageLower, def)) {
      return { definition: def, source: "explicit_ask" };
    }
  }

  // Pass 2: best-scoring phrase/concept match, skipping anything already seen.
  let best: { def: ChatRecommendationDefinition; score: number } | null = null;
  for (const def of CHAT_RECOMMENDATIONS) {
    if (!isRecommendationVisibleForPersona(def, persona)) continue;
    if (seenIds?.has(def.id)) continue;
    const { score, qualifies } = scoreDefinition(def, messageLower, tokens);
    if (!qualifies) continue;
    if (!best || score > best.score) best = { def, score };
  }

  if (!best) return null;
  return { definition: best.def, source: "client" };
}

/**
 * Resolve a recommendation given both the chat response (server hint) and the
 * user's most recent message (fallback). Server hints take precedence so the
 * agent can override or suppress client matches as it learns more about the
 * user. Persona gating and the seen-set are always enforced — except when
 * the user explicitly asks (typing the literal tool name).
 */
export function resolveChatRecommendation({
  response,
  userMessage,
  persona,
  seenIds,
}: {
  response: ChatResponse | undefined;
  userMessage: string;
  persona: WorkspacePersona;
  seenIds?: ReadonlySet<string>;
}): ResolvedRecommendation | null {
  const messageLower = (userMessage ?? "").toLowerCase();

  const hint = response?.metadata?.recommendation;
  if (hint?.id) {
    const def = getRecommendationById(hint.id);
    if (def && isRecommendationVisibleForPersona(def, persona)) {
      const isExplicit = messageMatchesAnyExplicitAsk(messageLower, def);
      if (isExplicit) {
        return { definition: def, source: "explicit_ask", reason: hint.reason };
      }
      if (!seenIds?.has(def.id)) {
        return { definition: def, source: "server", reason: hint.reason };
      }
      // Server suggested something the user has already declined; fall through
      // to the client matcher in case a *different* destination is now a fit.
    }
  }

  return matchRecommendationFromUserMessage(userMessage, { persona, seenIds });
}
